import {
  Alert,
  Button,
  Card,
  EmptyState,
  ResponsiveList,
  Skeleton,
  formatDate,
} from '@catering/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus, Wallet } from 'lucide-react';
import { type JSX, useState } from 'react';

import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import { formatPriceCents } from '../../contracts/format-price';
import {
  getAccountStatement,
  type AccountMovement,
  type AccountStatementParams,
} from '../../customer-account/api';
import { ACCOUNT_MOVEMENT_KIND_LABELS } from '../../customer-account/labels';
import { formatInvoiceNumber } from '../../invoices/format-invoice-number';

export interface CustomerAccountPageProps {
  customerId: string;
}

interface SummaryCard {
  label: string;
  cents: number;
}

/**
 * `customer-account-statement/spec.md` → "Pantallas de back-office para
 * cuenta corriente y antigüedad": one tab of `CustomerDetailPage`, visible
 * only with `payment.read`. `pendingBalanceCents`/`overdueAmountCents`/
 * `unallocatedCreditCents` always reflect the customer's current state,
 * unaffected by the date filter (`get-account-statement.use-case.ts`'s own
 * doc comment) — only `totalInvoicedCents`/`totalCollectedCents` and the
 * movement list itself narrow with `fromDate`/`toDate`.
 */
export function CustomerAccountPage({ customerId }: CustomerAccountPageProps): JSX.Element {
  const currentUser = useCurrentUser();
  const canRegisterPayment = hasPermission(currentUser.data, 'payment.register');

  const [fromDate, setFromDate] = useState<string | undefined>(undefined);
  const [toDate, setToDate] = useState<string | undefined>(undefined);

  const params: AccountStatementParams = { fromDate, toDate };

  const query = useQuery({
    queryKey: ['customer-account-statement', customerId, fromDate, toDate],
    queryFn: () => getAccountStatement(customerId, params),
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">Cuenta corriente</h2>
        {canRegisterPayment ? (
          <Link to="/payments/new" search={{ customerId }}>
            <Button icon={Plus}>Registrar recibo</Button>
          </Link>
        ) : null}
      </div>

      <Card>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Desde</span>
            <input
              type="date"
              value={fromDate ?? ''}
              onChange={(event) => setFromDate(event.target.value === '' ? undefined : event.target.value)}
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            />
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Hasta</span>
            <input
              type="date"
              value={toDate ?? ''}
              onChange={(event) => setToDate(event.target.value === '' ? undefined : event.target.value)}
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            />
          </label>
        </div>
      </Card>

      {query.isError ? (
        <Alert tone="danger" title="No pudimos cargar el estado de cuenta">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined ? (
        <>
          <SummaryCards summary={query.data.summary} />

          {query.data.movements.length === 0 ? (
            <EmptyState
              icon={Wallet}
              title="Todavía no hay movimientos registrados para esta empresa."
            />
          ) : (
            <ResponsiveList
              items={query.data.movements}
              rowKey={(movement) =>
                `${movement.kind}-${movement.date}-${movement.invoiceId ?? movement.paymentId ?? 'initial'}`
              }
              caption="Movimientos de la cuenta corriente"
              emptyState={null}
              columns={[
                {
                  key: 'kind',
                  header: 'Tipo',
                  render: (movement) => ACCOUNT_MOVEMENT_KIND_LABELS[movement.kind],
                },
                { key: 'date', header: 'Fecha', render: (movement) => formatDate(movement.date) },
                { key: 'document', header: 'Comprobante', render: (movement) => renderDocument(movement) },
                {
                  key: 'debit',
                  header: 'Débito',
                  render: (movement) => (movement.amountCents > 0 ? formatPriceCents(movement.amountCents) : '—'),
                },
                {
                  key: 'credit',
                  header: 'Crédito',
                  render: (movement) =>
                    movement.amountCents < 0 ? formatPriceCents(-movement.amountCents) : '—',
                },
                {
                  key: 'balance',
                  header: 'Saldo',
                  render: (movement) => formatPriceCents(movement.balanceAfterCents),
                },
              ]}
              renderCardHeader={(movement) => (
                <div className="flex items-center justify-between gap-2">
                  <span>{ACCOUNT_MOVEMENT_KIND_LABELS[movement.kind]}</span>
                  <span>{formatDate(movement.date)}</span>
                </div>
              )}
              renderCardBody={(movement) => (
                <div className="col-span-2 grid grid-cols-2 gap-2">
                  <span>{renderDocument(movement)}</span>
                  <span>
                    {movement.amountCents < 0
                      ? `Crédito: ${formatPriceCents(-movement.amountCents)}`
                      : `Débito: ${formatPriceCents(movement.amountCents)}`}
                  </span>
                  <span>Saldo: {formatPriceCents(movement.balanceAfterCents)}</span>
                </div>
              )}
            />
          )}
        </>
      ) : null}
    </div>
  );
}

/** Same `0001-A-00000001` format as Facturación; falls back to the bare number if the API did not send the point of sale/type. */
function invoiceLabel(movement: AccountMovement, invoiceNumber: string): string {
  if (movement.invoicePointOfSale == null || movement.invoiceType == null) {
    return invoiceNumber;
  }

  return formatInvoiceNumber(movement.invoicePointOfSale, movement.invoiceType, Number(invoiceNumber));
}

function renderDocument(movement: AccountMovement): JSX.Element | string {
  if (movement.kind === 'INVOICE' && movement.invoiceId !== null && movement.invoiceNumber !== null) {
    return (
      <Link to="/invoices/$id" params={{ id: movement.invoiceId }} className="font-medium text-slate-900 hover:underline">
        {invoiceLabel(movement, movement.invoiceNumber)}
      </Link>
    );
  }

  if (movement.kind === 'PAYMENT' && movement.paymentId !== null && movement.receiptNumber !== null) {
    return (
      <Link to="/payments/$id" params={{ id: movement.paymentId }} className="font-medium text-slate-900 hover:underline">
        {movement.receiptNumber}
      </Link>
    );
  }

  return '—';
}

function SummaryCards({
  summary,
}: {
  summary: {
    totalInvoicedCents: number;
    totalCollectedCents: number;
    pendingBalanceCents: number;
    overdueAmountCents: number;
    unallocatedCreditCents: number;
  };
}): JSX.Element {
  const cards: SummaryCard[] = [
    { label: 'Facturado', cents: summary.totalInvoicedCents },
    { label: 'Cobrado', cents: summary.totalCollectedCents },
    { label: 'Pendiente', cents: summary.pendingBalanceCents },
    { label: 'Vencido', cents: summary.overdueAmountCents },
    { label: 'Crédito no imputado', cents: summary.unallocatedCreditCents },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      {cards.map((card) => (
        <Card key={card.label}>
          <p className="text-sm font-medium text-slate-500">{card.label}</p>
          <p className="mt-1 text-xl font-semibold text-slate-900">{formatPriceCents(card.cents)}</p>
        </Card>
      ))}
    </div>
  );
}
